// src/pages/admin/TeacherManager.js
import React, { useState, useEffect, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { db, auth } from '../../firebase';
import { sendPasswordResetEmail } from 'firebase/auth';
import { getFunctions, httpsCallable } from 'firebase/functions';
import {
  collection,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  query,
  where,
  orderBy
} from 'firebase/firestore';
import {
  ArrowLeft,
  Plus,
  Edit2,
  UserX,
  UserCheck,
  ChevronDown,
  ChevronUp,
  AlertTriangle,
  CheckCircle,
  X,
  Users,
  Clock,
  Palette,
  RefreshCw,
  Trash2,
  KeyRound,
  FileText,
  Upload,
  BarChart3,
  GraduationCap
} from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { isSuperAdmin, getAdminRole } from '../../config/adminConfig';
import { COUNTRIES } from '../../config/locationData';
import {
  getBrowserTimezone,
  timeToUTC,
  timeFromUTC,
  formatTimeDisplay,
  COMMON_TIMEZONES,
  TOOLTIP_ZONES,
} from '../../utils/timezoneUtils';

// ─── Constants ────────────────────────────────────────────────────────────────

const DIALECTS = [
  { value: 'egyptian',  label: 'Egyptian' },
  { value: 'lebanese',  label: 'Lebanese' },
  { value: 'syrian',  label: 'Syrian' },
  { value: 'palestinian',  label: 'Palestinian' },
  { value: 'moroccan',  label: 'Moroccan' },
  { value: 'khaleeji',  label: 'Khaleeji' },
  { value: 'fusha',     label: 'Fusha (MSA)' },
  { value: 'other',     label: 'Other' },
];

const DAYS_OF_WEEK = [
  { value: 'sunday',    label: 'Sunday' },
  { value: 'monday',    label: 'Monday' },
  { value: 'tuesday',   label: 'Tuesday' },
  { value: 'wednesday', label: 'Wednesday' },
  { value: 'thursday',  label: 'Thursday' },
  { value: 'friday',    label: 'Friday' },
  { value: 'saturday',  label: 'Saturday' },
];

// Preset colors for teacher calendar coding (Phase 2)
const COLOR_PRESETS = [
  '#7C3AED', '#2563EB', '#059669', '#D97706',
  '#DC2626', '#DB2777', '#0891B2', '#65A30D',
];

const FUNCTIONS_BASE_URL = process.env.REACT_APP_FUNCTIONS_URL ||
  'https://us-central1-arabic-ai-kids.cloudfunctions.net';

const EMPTY_TEACHER = {
  name: '',
  email: '',
  phone: '',
  timezone: '',
  country: '',
  paymentMethod: '',
  paymentDetail: '',
  dialect: 'egyptian',
  color: COLOR_PRESETS[0],
  availability: [],
  active: true,
};

const EMPTY_AVAILABILITY_SLOT = {
  dayOfWeek: 'monday',
  startTime: '16:00',
  endTime: '17:00',
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

function dialectLabel(value) {
  return DIALECTS.find(d => d.value === value)?.label ?? value;
}

function dayLabel(value) {
  return DAYS_OF_WEEK.find(d => d.value === value)?.label ?? value;
}

// Formats a raw HH:MM string as 12-hour time — used for display only.
// For UTC-stored times, pair with formatTimeDisplay(utcStr, displayTz) from timezoneUtils instead.
function formatTime(t) {
  if (!t) return '';
  const [h, m] = t.split(':');
  const hour = parseInt(h, 10);
  const ampm = hour >= 12 ? 'PM' : 'AM';
  const display = hour % 12 === 0 ? 12 : hour % 12;
  return `${display}:${m} ${ampm}`;
}

// ─── Timezone Tooltip ─────────────────────────────────────────────────────────
// All times are stored as UTC HH:MM strings (post-migration).
// This tooltip converts a UTC slot time to common reference timezones so staff
// in Cairo, Vancouver, London etc. can see the equivalent local time at a glance.

function getTimezoneConversions(utcTimeStr, utcDayName) {
  if (!utcTimeStr || !utcDayName) return [];
  try {
    return TOOLTIP_ZONES.map(({ label, tz }) => {
      const { localDay, dayChanged } = timeFromUTC(utcTimeStr, utcDayName, tz);
      return {
        label,
        time:    formatTimeDisplay(utcTimeStr, tz),
        nextDay: dayChanged,
      };
    });
  } catch {
    return [];
  }
}

function TimezoneTooltip({ timeStr, dayOfWeek, children }) {
  const conversions = getTimezoneConversions(timeStr, dayOfWeek);
  const [visible, setVisible] = useState(false);
  const [coords,  setCoords]  = useState({ top: 0, left: 0 });
  const triggerRef            = useRef(null);

  // Detect touch-only devices once on mount — never changes during the session.
  const [isTouch] = useState(() => window.matchMedia('(hover: none)').matches);

  const show = useCallback(() => {
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    setCoords({
      top:  rect.top + window.scrollY - 8,
      left: rect.left + window.scrollX + rect.width / 2,
    });
    setVisible(true);
  }, []);

  const hide = useCallback(() => setVisible(false), []);

  const toggle = useCallback(() => {
    if (visible) hide(); else show();
  }, [visible, show, hide]);

  if (!conversions.length) return <>{children}</>;

  const tooltip = visible && ReactDOM.createPortal(
    <>
      {/* Touch-only: full-screen overlay closes the tooltip when tapped */}
      {isTouch && (
        <div
          className="fixed inset-0 z-[9998] bg-black bg-opacity-20"
          style={{ pointerEvents: 'auto' }}
          onClick={hide}
        />
      )}
      <div
        className="fixed z-[9999]"
        style={{
          top:           coords.top,
          left:          coords.left,
          transform:     'translate(-50%, -100%)',
          pointerEvents: isTouch ? 'auto' : 'none',
        }}
      >
        <div
          className="bg-gray-900 text-white text-xs rounded-lg shadow-2xl p-3 relative"
          style={{ minWidth: 200 }}
        >
          {/* Touch-only: × close button inside the panel */}
          {isTouch && (
            <button
              onClick={hide}
              className="absolute top-1.5 right-1.5 text-gray-400 hover:text-white p-0.5"
            >
              <X size={12} />
            </button>
          )}
          <span className="block text-gray-400 uppercase tracking-wider mb-2 font-semibold" style={{ fontSize: 10 }}>
            🌍 Time zone reference
          </span>
          {conversions.map(({ label, time, nextDay }) => (
            <span key={label} className="flex justify-between gap-4 leading-6">
              <span className="text-gray-400">{label}</span>
              <span className="font-semibold text-white whitespace-nowrap">
                {time}
                {nextDay && <span className="ml-1 text-yellow-400" style={{ fontSize: 10 }}>+1d</span>}
              </span>
            </span>
          ))}
          <div
            className="absolute left-1/2 bg-gray-900 rotate-45"
            style={{ top: '100%', marginTop: -4, marginLeft: -4, width: 8, height: 8 }}
          />
        </div>
      </div>
    </>,
    document.body
  );

  return (
    <>
      <span
        ref={triggerRef}
        {...(isTouch
          ? { onClick: toggle }
          : { onMouseEnter: show, onMouseLeave: hide }
        )}
        className="inline-flex items-center"
      >
        {children}
      </span>
      {tooltip}
    </>
  );
}

// ─── Sub-components ───────────────────────────────────────────────────────────

function StatusBadge({ active }) {
  return active ? (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
      <UserCheck size={11} /> Active
    </span>
  ) : (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-500">
      <UserX size={11} /> Inactive
    </span>
  );
}

// Availability slot row inside the form.
// slot times are in the admin's entry timezone (local); conversion to UTC happens on submit.
function AvailabilityRow({ slot, index, onChange, onRemove, entryTimezone }) {
  // Compute live UTC preview for this slot
  const startUTC = timeToUTC(slot.startTime, slot.dayOfWeek, entryTimezone);
  const endUTC   = timeToUTC(slot.endTime,   slot.dayOfWeek, entryTimezone);
  const dayShift = startUTC.utcDay !== slot.dayOfWeek;

  return (
    <div className="space-y-1.5">
      <div className="flex items-center gap-2 flex-wrap">
        <select
          value={slot.dayOfWeek}
          onChange={e => onChange(index, 'dayOfWeek', e.target.value)}
          className="border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-purple-500"
        >
          {DAYS_OF_WEEK.map(d => (
            <option key={d.value} value={d.value}>{d.label}</option>
          ))}
        </select>
        <input
          type="time"
          value={slot.startTime}
          onChange={e => onChange(index, 'startTime', e.target.value)}
          className="border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-purple-500"
        />
        <span className="text-gray-400 text-sm">to</span>
        <input
          type="time"
          value={slot.endTime}
          onChange={e => onChange(index, 'endTime', e.target.value)}
          className="border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-purple-500"
        />
        <button
          type="button"
          onClick={() => onRemove(index)}
          className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
        >
          <X size={14} />
        </button>
      </div>
      {/* Live UTC preview */}
      <p className="text-xs text-gray-400 pl-1">
        = {startUTC.utcTime}–{endUTC.utcTime} UTC
        {' · '}
        {startUTC.utcDay.charAt(0).toUpperCase() + startUTC.utcDay.slice(1)}
        {dayShift && (
          <span className="ml-1.5 text-yellow-600 font-medium">⚠ day shifts in UTC</span>
        )}
      </p>
    </div>
  );
}

// ─── Teacher Form Modal ────────────────────────────────────────────────────────

function TeacherFormModal({ teacher, onSave, onClose, saving }) {
  const isEditing = !!teacher?.id;
  const browserTz = getBrowserTimezone();

  // When editing an existing teacher, convert stored UTC availability times back
  // to the browser's local timezone so the admin sees their local times in the form.
  function toLocalAvailability(availability, tz) {
    return (availability ?? []).map(slot => {
      const { localTime: startTime, localDay: dayOfWeek } = timeFromUTC(slot.startTime, slot.dayOfWeek, tz);
      const { localTime: endTime }                        = timeFromUTC(slot.endTime,   slot.dayOfWeek, tz);
      return { dayOfWeek, startTime, endTime };
    });
  }

  const [form, setForm] = useState(
    teacher
      ? { ...teacher, availability: toLocalAvailability(teacher.availability, browserTz) }
      : { ...EMPTY_TEACHER, color: COLOR_PRESETS[Math.floor(Math.random() * COLOR_PRESETS.length)] }
  );
  const [errors, setErrors]           = useState({});
  const [entryTimezone, setEntryTimezone] = useState(browserTz);

  function setField(field, value) {
    setForm(f => ({ ...f, [field]: value }));
    setErrors(e => { const n = { ...e }; delete n[field]; return n; });
  }

  function addAvailabilitySlot() {
    setForm(f => ({
      ...f,
      availability: [...(f.availability ?? []), { ...EMPTY_AVAILABILITY_SLOT }]
    }));
  }

  function updateAvailabilitySlot(index, field, value) {
    setForm(f => {
      const updated = [...f.availability];
      updated[index] = { ...updated[index], [field]: value };
      return { ...f, availability: updated };
    });
  }

  function removeAvailabilitySlot(index) {
    setForm(f => ({
      ...f,
      availability: f.availability.filter((_, i) => i !== index)
    }));
  }

  // When the admin changes the entry timezone dropdown, round-trip all current
  // slot times through UTC so the displayed values update to the new timezone.
  function handleTimezoneChange(newTz) {
    setForm(f => ({
      ...f,
      availability: (f.availability ?? []).map(slot => {
        const { utcTime: startUTC, utcDay } = timeToUTC(slot.startTime, slot.dayOfWeek, entryTimezone);
        const { utcTime: endUTC }           = timeToUTC(slot.endTime,   slot.dayOfWeek, entryTimezone);
        const { localTime: startTime, localDay: dayOfWeek } = timeFromUTC(startUTC, utcDay, newTz);
        const { localTime: endTime }                        = timeFromUTC(endUTC,   utcDay, newTz);
        return { dayOfWeek, startTime, endTime };
      }),
    }));
    setEntryTimezone(newTz);
  }

  function validate() {
    const e = {};
    if (!form.name?.trim()) e.name = 'Name is required';
    if (!form.email?.trim()) {
      e.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) {
      e.email = 'Enter a valid email address';
    }
    if (!form.dialect) e.dialect = 'Dialect is required';

    // validate() runs against local times in the form — correct, validate before converting
    const slots = form.availability ?? [];

    // Pass 1: each slot's own time order
    slots.forEach((slot, i) => {
      if (slot.startTime >= slot.endTime) {
        e[`avail_${i}`] = 'End time must be after start time';
      }
    });

    // Pass 2: overlap check — only between slots on the same local day
    slots.forEach((slotA, i) => {
      slots.forEach((slotB, j) => {
        if (j <= i) return;
        if (slotA.dayOfWeek !== slotB.dayOfWeek) return;
        const overlaps = slotA.startTime < slotB.endTime &&
                         slotB.startTime < slotA.endTime;
        if (overlaps) {
          const day = slotA.dayOfWeek.charAt(0).toUpperCase() + slotA.dayOfWeek.slice(1);
          e[`avail_${i}`] = `Overlaps with another ${day} slot`;
          e[`avail_${j}`] = `Overlaps with another ${day} slot`;
        }
      });
    });

    setErrors(e);
    return Object.keys(e).length === 0;
  }

  function handleSubmit(e) {
    e.preventDefault();
    if (!validate()) return;

    // Convert all slot times from entryTimezone → UTC before saving
    const utcAvailability = (form.availability ?? []).map(slot => {
      const { utcTime: startTime, utcDay: dayOfWeek } = timeToUTC(slot.startTime, slot.dayOfWeek, entryTimezone);
      const { utcTime: endTime }                      = timeToUTC(slot.endTime,   slot.dayOfWeek, entryTimezone);
      return { dayOfWeek, startTime, endTime };
    });

    onSave({ ...form, availability: utcAvailability });
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
        {/* Modal header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-800">
            {isEditing ? 'Edit Teacher' : 'Add Teacher'}
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X size={18} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          {/* Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Full Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={form.name}
              onChange={e => setField('name', e.target.value)}
              placeholder="e.g. Fatima Al-Rashid"
              className={`w-full border rounded-lg px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-purple-500 ${
                errors.name ? 'border-red-400' : 'border-gray-300'
              }`}
            />
            {errors.name && <p className="text-xs text-red-500 mt-1">{errors.name}</p>}
          </div>

          {/* Email (required — used to provision the teacher's login) */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Email <span className="text-red-500">*</span>
            </label>
            <input
              type="email"
              value={form.email ?? ''}
              onChange={e => setField('email', e.target.value)}
              placeholder="e.g. teacher@example.com"
              className={`w-full border rounded-lg px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-purple-500 ${
                errors.email ? 'border-red-400' : 'border-gray-300'
              }`}
            />
            {errors.email && <p className="text-xs text-red-500 mt-1">{errors.email}</p>}
          </div>

          {/* Phone (optional) */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
            <input
              type="tel"
              value={form.phone ?? ''}
              onChange={e => setField('phone', e.target.value)}
              placeholder="e.g. +1 250 555 0123"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-purple-500"
            />
          </div>

          {/* Timezone (optional) */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Timezone</label>
            <select
              value={form.timezone ?? ''}
              onChange={e => setField('timezone', e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-purple-500"
            >
              <option value="">Select timezone</option>
              {COMMON_TIMEZONES.map(tz => (
                <option key={tz.value} value={tz.value}>{tz.label}</option>
              ))}
            </select>
          </div>

          {/* Country (optional) */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Country</label>
            <select
              value={form.country ?? ''}
              onChange={e => setField('country', e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-purple-500"
            >
              <option value="">Select country</option>
              {COUNTRIES.map(c => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </div>

          {/* Payment Method (admin only) */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Payment Method</label>
            <select
              value={form.paymentMethod ?? ''}
              onChange={e => setField('paymentMethod', e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-purple-500"
            >
              <option value="">Select payment method</option>
              <option value="Western Union">Western Union</option>
              <option value="Bank Transfer">Bank Transfer</option>
              <option value="PayPal">PayPal</option>
              <option value="Wise">Wise</option>
              <option value="Cash">Cash</option>
              <option value="Other">Other</option>
            </select>
          </div>

          {/* Payment Details (admin only) */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Payment Details</label>
            <textarea
              value={form.paymentDetail ?? ''}
              onChange={e => setField('paymentDetail', e.target.value)}
              rows={3}
              placeholder="Bank account, ID, or other payment details"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-purple-500"
            />
          </div>


          {/* Dialect */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Dialect <span className="text-red-500">*</span>
            </label>
            <select
              value={form.dialect}
              onChange={e => setField('dialect', e.target.value)}
              className={`w-full border rounded-lg px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-purple-500 ${
                errors.dialect ? 'border-red-400' : 'border-gray-300'
              }`}
            >
              {DIALECTS.map(d => (
                <option key={d.value} value={d.value}>{d.label}</option>
              ))}
            </select>
          </div>

          {/* Calendar color (Phase 2 use) */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <span className="flex items-center gap-1.5">
                <Palette size={14} />
                Calendar Color
                <span className="text-xs text-gray-400 font-normal">(used in Phase 2 scheduling calendar)</span>
              </span>
            </label>
            <div className="flex items-center gap-2 flex-wrap">
              {COLOR_PRESETS.map(color => (
                <button
                  key={color}
                  type="button"
                  onClick={() => setField('color', color)}
                  className={`w-7 h-7 rounded-full border-2 transition-all ${
                    form.color === color ? 'border-gray-800 scale-110' : 'border-transparent'
                  }`}
                  style={{ backgroundColor: color }}
                />
              ))}
              {/* Custom hex input */}
              <input
                type="color"
                value={form.color}
                onChange={e => setField('color', e.target.value)}
                className="w-7 h-7 rounded-full cursor-pointer border border-gray-300"
                title="Custom color"
              />
            </div>
          </div>

          {/* Availability */}
          <div>
            {/* Header row: label + timezone selector + add button */}
            <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
              <label className="block text-sm font-medium text-gray-700">
                <span className="flex items-center gap-1.5">
                  <Clock size={14} />
                  Availability
                </span>
              </label>
              <div className="flex items-center gap-2">
                {/* Entry timezone dropdown — times in the form are shown in this timezone */}
                <select
                  value={entryTimezone}
                  onChange={e => handleTimezoneChange(e.target.value)}
                  className="border border-gray-300 rounded-lg px-2 py-1.5 text-xs text-gray-700 focus:outline-none focus:ring-2 focus:ring-purple-500"
                  title="Times are entered in this timezone and converted to UTC on save"
                >
                  {COMMON_TIMEZONES.map(tz => (
                    <option key={tz.value} value={tz.value}>{tz.label}</option>
                  ))}
                </select>
                <button
                  type="button"
                  onClick={addAvailabilitySlot}
                  className="flex items-center gap-1 text-xs text-purple-600 hover:text-purple-700 font-medium"
                >
                  <Plus size={13} /> Add slot
                </button>
              </div>
            </div>
            {form.availability?.length === 0 ? (
              <p className="text-xs text-gray-400 italic">No availability slots added yet.</p>
            ) : (
              <div className="space-y-2">
                {form.availability.map((slot, i) => (
                  <div key={i}>
                    <AvailabilityRow
                      slot={slot}
                      index={i}
                      onChange={updateAvailabilitySlot}
                      onRemove={removeAvailabilitySlot}
                      entryTimezone={entryTimezone}
                    />
                    {errors[`avail_${i}`] && (
                      <p className="text-xs text-red-500 mt-1">{errors[`avail_${i}`]}</p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Active toggle (edit only) */}
          {isEditing && (
            <div className="flex items-center justify-between py-3 border-t border-gray-100">
              <div>
                <p className="text-sm font-medium text-gray-700">Active</p>
                <p className="text-xs text-gray-400">Inactive teachers won't appear in class assignment dropdowns</p>
              </div>
              <button
                type="button"
                onClick={() => setField('active', !form.active)}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  form.active ? 'bg-purple-600' : 'bg-gray-300'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                    form.active ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="flex-1 px-4 py-2 bg-purple-600 text-white text-sm font-medium rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {saving && <RefreshCw size={14} className="animate-spin" />}
              {isEditing ? 'Save Changes' : 'Add Teacher'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// ─── Reassign Triage Modal ─────────────────────────────────────────────────────
// Shows when a teacher is being deactivated and still has active classes.
// Admin resolves each class one at a time before the teacher goes inactive.

function ReassignTriageModal({ teacher, affectedClasses, activeTeachers, onResolve, onClose }) {
  // resolvedMap: { classId: { action: 'reassign'|'suspend', replacementTeacherId? } }
  const [resolvedMap, setResolvedMap] = useState({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const allResolved = affectedClasses.every(c => resolvedMap[c.id]);
  const browserTz   = getBrowserTimezone();

  function setClassResolution(classId, action, replacementTeacherId = null) {
    setResolvedMap(m => ({
      ...m,
      [classId]: { action, replacementTeacherId }
    }));
  }

  async function handleConfirm() {
    setSaving(true);
    setError(null);
    try {
      await onResolve(resolvedMap);
    } catch (err) {
      console.error('Triage resolve error:', err);
      setError('Something went wrong. Please try again.');
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-start justify-between">
            <div>
              <h2 className="text-lg font-semibold text-gray-800">
                Resolve Classes for {teacher.name}
              </h2>
              <p className="text-sm text-gray-500 mt-1">
                This teacher has {affectedClasses.length} active{' '}
                {affectedClasses.length === 1 ? 'class' : 'classes'}.
                Resolve each one before marking them inactive.
              </p>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
              <X size={18} />
            </button>
          </div>
        </div>

        {/* Class list */}
        <div className="p-6 space-y-4">
          {affectedClasses.map(cls => {
            const resolution = resolvedMap[cls.id];
            const resolved = !!resolution;

            // cls.dayOfWeek and cls.time are stored as UTC — convert for display
            const { localTime: displayTime, localDay: displayDay } =
              timeFromUTC(cls.time ?? '00:00', cls.dayOfWeek ?? 'monday', browserTz);

            return (
              <div
                key={cls.id}
                className={`border rounded-xl p-4 transition-colors ${
                  resolved ? 'border-green-200 bg-green-50' : 'border-gray-200 bg-white'
                }`}
              >
                {/* Class info */}
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <p className="font-medium text-gray-800">
                      {dayLabel(displayDay)} at {formatTimeDisplay(cls.time, browserTz)}
                    </p>
                    <p className="text-xs text-gray-500 mt-0.5">
                      {dialectLabel(cls.dialect)} · {cls.type} · {cls.ageGroup}
                      {cls.levelGroup ? ` · ${cls.levelGroup}` : ''}
                    </p>
                    <p className="text-xs text-gray-500 mt-0.5 flex items-center gap-1">
                      <Users size={11} />
                      {cls.enrolledStudents?.length ?? 0} student
                      {(cls.enrolledStudents?.length ?? 0) !== 1 ? 's' : ''}:&nbsp;
                      {(cls.enrolledStudents ?? []).map(s => s.childName).join(', ')}
                    </p>
                  </div>
                  {resolved && (
                    <CheckCircle size={18} className="text-green-500 flex-shrink-0" />
                  )}
                </div>

                {/* Resolution controls */}
                {!resolved ? (
                  <div className="flex gap-2 flex-wrap">
                    {/* Reassign to another teacher */}
                    <select
                      defaultValue=""
                      onChange={e => {
                        if (e.target.value) {
                          setClassResolution(cls.id, 'reassign', e.target.value);
                        }
                      }}
                      className="flex-1 min-w-[180px] border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-purple-500"
                    >
                      <option value="">Reassign to teacher…</option>
                      {activeTeachers
                        .filter(t => t.id !== teacher.id)
                        .map(t => (
                          <option key={t.id} value={t.id}>
                            {t.name} ({dialectLabel(t.dialect)})
                          </option>
                        ))}
                    </select>

                    {/* Or suspend */}
                    <button
                      type="button"
                      onClick={() => setClassResolution(cls.id, 'suspend')}
                      className="px-3 py-2 border border-orange-300 text-orange-700 text-sm rounded-lg hover:bg-orange-50 transition-colors"
                    >
                      Suspend class
                    </button>
                  </div>
                ) : (
                  <div className="flex items-center justify-between">
                    <p className="text-sm text-green-700">
                      {resolution.action === 'reassign'
                        ? `Reassigned → ${activeTeachers.find(t => t.id === resolution.replacementTeacherId)?.name ?? 'Unknown'}`
                        : 'Will be suspended'}
                    </p>
                    <button
                      type="button"
                      onClick={() => {
                        setResolvedMap(m => {
                          const n = { ...m };
                          delete n[cls.id];
                          return n;
                        });
                      }}
                      className="text-xs text-gray-400 hover:text-gray-600 underline"
                    >
                      Undo
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {error && (
          <div className="mx-6 mb-4 px-4 py-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
            {error}
          </div>
        )}

        {/* Footer */}
        <div className="p-6 border-t border-gray-200 flex gap-3">
          <button
            onClick={onClose}
            className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-50 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleConfirm}
            disabled={!allResolved || saving}
            className="flex-1 px-4 py-2 bg-purple-600 text-white text-sm font-medium rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {saving && <RefreshCw size={14} className="animate-spin" />}
            Confirm & Mark Inactive
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Teacher Login Modal ───────────────────────────────────────────────────────
function AccountBadge({ teacher }) {
  const hasAccount = !!teacher.authUid;
  const hasLoggedIn = !!teacher.lastLoginAt;
  let cfg;
  if (!hasAccount) {
    cfg = { label: 'No login', text: 'text-gray-500', bg: 'bg-gray-100', title: 'No login account yet — use the key icon to create one' };
  } else if (!hasLoggedIn) {
    cfg = { label: 'Invited', text: 'text-amber-700', bg: 'bg-amber-100', title: 'Account created, but the teacher has not logged in yet' };
  } else {
    cfg = { label: 'Active', text: 'text-green-700', bg: 'bg-green-100', title: 'Teacher has logged in' };
  }
  return (
    <span title={cfg.title} className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium ${cfg.bg} ${cfg.text}`}>
      {cfg.label}
    </span>
  );
}

function StatCard({ icon, label, value }) {
  return (
    <div className="bg-purple-50 rounded-xl p-3 text-center">
      <div className="flex justify-center text-purple-600 mb-1">{icon}</div>
      <div className="text-2xl font-bold text-gray-800">{value}</div>
      <div className="text-xs text-gray-500">{label}</div>
    </div>
  );
}

function TeacherStatsModal({ teacher, onClose }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true); setError(null);
      try {
        const tz = teacher.timezone || 'UTC';

        const classSnap = await getDocs(query(collection(db, 'classes'), where('teacherId', '==', teacher.id)));
        const classes = classSnap.docs.map(d => ({ id: d.id, ...d.data() })).filter(c => c.status === 'active');

        const subSnap = await getDocs(query(collection(db, 'sessions'), where('substituteTeacherId', '==', teacher.id)));
        const sessionsSubbed = subSnap.size;

        const auditSnap = await getDocs(query(collection(db, 'teacherAuditLog'), where('teacherId', '==', teacher.id)));
        const audit = auditSnap.docs.map(d => {
          const x = d.data();
          return { ...x, when: x.timestamp && x.timestamp.toDate ? x.timestamp.toDate() : null };
        });

        const studentIds = new Set();
        classes.forEach(c => (c.enrolledStudents || []).forEach(s => { if (s.childId) studentIds.add(s.childId); }));

        const DAYS = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
        const dayCounts = Object.fromEntries(DAYS.map(d => [d, 0]));
        classes.forEach(c => {
          const slots = (c.slots && c.slots.length > 0) ? c.slots : (c.dayOfWeek && c.time ? [{ dayOfWeek: c.dayOfWeek, time: c.time }] : []);
          slots.forEach(s => {
            let day = s.dayOfWeek;
            try { const r = timeFromUTC(s.time, s.dayOfWeek, tz); day = r.localDay || s.dayOfWeek; } catch (e) { /* keep utc day */ }
            if (dayCounts[day] !== undefined) dayCounts[day] += 1;
          });
        });
        const dayData = DAYS.map(d => ({ day: d.slice(0, 3).replace(/^./, ch => ch.toUpperCase()), fullDay: d, count: dayCounts[d] }));
        const taughtDays = dayData.filter(d => d.count > 0);
        const busiest = taughtDays.length ? taughtDays.reduce((a, b) => (b.count > a.count ? b : a)) : null;
        const lightest = taughtDays.length ? taughtDays.reduce((a, b) => (b.count < a.count ? b : a)) : null;

        const marked = audit.filter(a => a.action === 'attendance_taken');
        const markedSessions = new Set(marked.map(a => a.sessionId).filter(Boolean));
        const lastAttendance = marked.reduce((max, a) => ((a.when && (!max || a.when > max)) ? a.when : max), null);
        const classTitleById = Object.fromEntries(classes.map(c => [c.id, c.title || 'Class']));

        if (!cancelled) {
          setStats({
            classesTaught: classes.length,
            distinctStudents: studentIds.size,
            sessionsSubbed,
            sessionsMarked: markedSessions.size,
            dayData, busiest, lightest, lastAttendance,
            audit, classTitleById,
          });
        }
      } catch (err) {
        if (!cancelled) setError('Could not load stats.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [teacher.id, teacher.timezone]);

  const lastLogin = teacher.lastLoginAt && teacher.lastLoginAt.toDate ? teacher.lastLoginAt.toDate() : null;
  const relTime = (d) => {
    if (!d) return 'Never';
    const days = Math.floor((Date.now() - d.getTime()) / 86400000);
    if (days <= 0) return 'Today';
    if (days === 1) return 'Yesterday';
    if (days < 30) return `${days} days ago`;
    return d.toLocaleDateString();
  };
  const capDay = (s) => (s ? s.replace(/^./, c => c.toUpperCase()) : s);

  const auditData = stats?.audit || [];
  const weeklyActivity = (() => {
    const marks = auditData.filter(a => a.action === 'attendance_taken' && a.when);
    const WEEKS = 8;
    const now = new Date();
    const buckets = [];
    for (let i = WEEKS - 1; i >= 0; i--) {
      const end = new Date(now); end.setDate(end.getDate() - i * 7);
      const start = new Date(end); start.setDate(start.getDate() - 7);
      buckets.push({ label: end.toLocaleDateString('en-US', { month: 'short', day: 'numeric' }), start, end, count: 0 });
    }
    marks.forEach(a => {
      for (const b of buckets) {
        if (a.when > b.start && a.when <= b.end) { b.count += 1; break; }
      }
    });
    return buckets.map(b => ({ label: b.label, count: b.count }));
  })();
  const recentActivity = [...auditData].filter(a => a.when).sort((a, b) => b.when - a.when).slice(0, 30);
  const activityLabel = (a) => {
    const title = stats?.classTitleById?.[a.classId];
    switch (a.action) {
      case 'login': return 'Logged in';
      case 'attendance_taken': return `Marked ${a.studentCount ?? 0} student${a.studentCount === 1 ? '' : 's'}${title ? ` in ${title}` : ''}`;
      case 'provision_account': return 'Login account created';
      case 'temp_password_set': return 'Password set by admin';
      case 'document_uploaded': return `Document added${a.fileName ? `: ${a.fileName}` : ''}`;
      case 'document_deleted': return `Document removed${a.fileName ? `: ${a.fileName}` : ''}`;
      default: return a.action || 'Activity';
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-2xl max-h-[88vh] flex flex-col" onClick={e => e.stopPropagation()}>
        <div className="flex items-start justify-between p-5 border-b border-gray-100">
          <div>
            <h3 className="text-lg font-bold text-gray-800">Teacher Stats</h3>
            <p className="text-sm text-gray-500">{teacher.name}</p>
          </div>
          <button onClick={onClose} className="p-1 text-gray-400 hover:text-gray-600"><X size={18} /></button>
        </div>

        <div className="p-5 overflow-y-auto">
          {loading && <p className="text-sm text-gray-500 py-10 text-center">Loading stats…</p>}
          {!loading && error && <p className="text-sm text-red-600 py-4">{error}</p>}
          {!loading && !error && stats && (
            <div className="space-y-5">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <StatCard icon={<GraduationCap size={18} />} label="Classes" value={stats.classesTaught} />
                <StatCard icon={<Users size={18} />} label="Students" value={stats.distinctStudents} />
                <StatCard icon={<CheckCircle size={18} />} label="Sessions Marked" value={stats.sessionsMarked} />
                <StatCard icon={<RefreshCw size={18} />} label="Sessions Subbed" value={stats.sessionsSubbed} />
              </div>

              <div className="border border-gray-200 rounded-xl p-4">
                <h4 className="text-sm font-semibold text-gray-700 mb-1">Classes by day of week</h4>
                {stats.busiest ? (
                  <p className="text-xs text-gray-500 mb-3">
                    Busiest: <span className="font-medium text-gray-700">{capDay(stats.busiest.fullDay)} ({stats.busiest.count})</span>
                    {stats.lightest && stats.lightest.fullDay !== stats.busiest.fullDay && (
                      <> · Lightest: <span className="font-medium text-gray-700">{capDay(stats.lightest.fullDay)} ({stats.lightest.count})</span></>
                    )}
                  </p>
                ) : <p className="text-xs text-gray-400 mb-3">No classes scheduled.</p>}
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={stats.dayData}>
                    <XAxis dataKey="day" tick={{ fontSize: 12 }} />
                    <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
                    <Tooltip />
                    <Bar dataKey="count" radius={[4, 4, 0, 0]}>
                      {stats.dayData.map((d, i) => (
                        <Cell key={i} fill={stats.busiest && d.fullDay === stats.busiest.fullDay ? '#7c3aed' : '#c4b5fd'} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="border border-gray-200 rounded-xl p-4">
                  <div className="text-xs text-gray-400 uppercase tracking-wide">Last login</div>
                  <div className="text-sm font-medium text-gray-700 mt-1">{relTime(lastLogin)}</div>
                </div>
                <div className="border border-gray-200 rounded-xl p-4">
                  <div className="text-xs text-gray-400 uppercase tracking-wide">Last attendance</div>
                  <div className="text-sm font-medium text-gray-700 mt-1">{relTime(stats.lastAttendance)}</div>
                </div>
              </div>

              <div className="border border-gray-200 rounded-xl p-4">
                <h4 className="text-sm font-semibold text-gray-700 mb-3">Attendance activity (last 8 weeks)</h4>
                <ResponsiveContainer width="100%" height={180}>
                  <BarChart data={weeklyActivity}>
                    <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                    <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
                    <Tooltip />
                    <Bar dataKey="count" fill="#10b981" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>

              <div className="border border-gray-200 rounded-xl p-4">
                <h4 className="text-sm font-semibold text-gray-700 mb-3">Recent activity</h4>
                {recentActivity.length === 0 ? (
                  <p className="text-sm text-gray-400">No activity recorded yet.</p>
                ) : (
                  <ul className="divide-y divide-gray-100">
                    {recentActivity.map((a, i) => (
                      <li key={i} className="py-2 flex items-center justify-between gap-3">
                        <span className="text-sm text-gray-700">{activityLabel(a)}</span>
                        <span className="text-xs text-gray-400 shrink-0">{a.when ? a.when.toLocaleDateString() : ''}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          )}
        </div>

        <div className="p-4 border-t border-gray-100">
          <button onClick={onClose} className="w-full py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-lg transition-colors">Done</button>
        </div>
      </div>
    </div>
  );
}

function TeacherDocumentsModal({ teacher, onClose }) {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [file, setFile] = useState(null);
  const [notes, setNotes] = useState('');
  const [uploading, setUploading] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const loadDocs = async () => {
    setLoading(true); setError(null);
    try {
      const snap = await getDocs(collection(db, 'teachers', teacher.id, 'documents'));
      const list = snap.docs.map(d => ({ id: d.id, ...d.data() }))
        .sort((a, b) => (b.uploadedAt || '').localeCompare(a.uploadedAt || ''));
      setDocs(list);
    } catch (err) {
      setError('Could not load documents.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadDocs(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [teacher.id]);

  const handleUpload = async () => {
    if (!file) { setError('Choose a PDF first.'); return; }
    if (file.type !== 'application/pdf') { setError('Only PDF files are allowed.'); return; }
    setUploading(true); setError(null);
    try {
      const token = await auth.currentUser.getIdToken();
      const formData = new FormData();
      formData.append('teacherId', teacher.id);
      formData.append('notes', notes);
      formData.append('token', token);
      formData.append('file', file);
      const res = await fetch(`${FUNCTIONS_BASE_URL}/uploadTeacherDocument`, { method: 'POST', body: formData });
      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.error || 'Upload failed.');
      }
      setFile(null); setNotes('');
      await loadDocs();
    } catch (err) {
      setError(err.message || 'Upload failed.');
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (docId) => {
    if (!window.confirm('Delete this document? This cannot be undone.')) return;
    setDeletingId(docId); setError(null);
    try {
      const fns = getFunctions();
      await httpsCallable(fns, 'deleteTeacherDocument')({ teacherId: teacher.id, documentId: docId });
      await loadDocs();
    } catch (err) {
      setError(err.message || 'Delete failed.');
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[85vh] flex flex-col" onClick={e => e.stopPropagation()}>
        <div className="flex items-start justify-between p-5 border-b border-gray-100">
          <div>
            <h3 className="text-lg font-bold text-gray-800">Documents</h3>
            <p className="text-sm text-gray-500">{teacher.name}</p>
          </div>
          <button onClick={onClose} className="p-1 text-gray-400 hover:text-gray-600"><X size={18} /></button>
        </div>

        <div className="p-5 overflow-y-auto space-y-4">
          <div className="border border-gray-200 rounded-lg p-3 space-y-2">
            <label className="block text-xs font-medium text-gray-600">Upload a PDF (contract, ID, etc.)</label>
            <input
              id="teacherDocFileInput"
              type="file"
              accept="application/pdf,.pdf"
              onChange={e => setFile(e.target.files?.[0] || null)}
              style={{ position: 'absolute', width: 1, height: 1, minHeight: 0, padding: 0, margin: -1, overflow: 'hidden', clip: 'rect(0, 0, 0, 0)', whiteSpace: 'nowrap', border: 0 }}
            />
            <div className="flex items-center gap-2">
              <label
                htmlFor="teacherDocFileInput"
                className="px-3 py-1.5 bg-white border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50 font-medium shrink-0 cursor-pointer"
              >
                Choose PDF
              </label>
              <span className="text-sm text-gray-500 truncate">{file ? file.name : 'No file chosen'}</span>
            </div>
            <textarea
              value={notes}
              onChange={e => setNotes(e.target.value)}
              rows={2}
              placeholder="Note for this document (optional)"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
            />
            <button
              onClick={handleUpload}
              disabled={uploading || !file}
              className="w-full py-2 bg-purple-600 text-white text-sm font-medium rounded-lg hover:bg-purple-700 disabled:opacity-50 flex items-center justify-center gap-2"
            >
              <Upload size={15} /> {uploading ? 'Uploading…' : 'Upload PDF'}
            </button>
          </div>

          {loading && <p className="text-sm text-gray-500 py-4 text-center">Loading…</p>}
          {!loading && docs.length === 0 && <p className="text-sm text-gray-400 py-4 text-center">No documents uploaded yet.</p>}
          {!loading && docs.length > 0 && (
            <ul className="space-y-2">
              {docs.map(d => (
                <li key={d.id} className="border border-gray-200 rounded-lg p-3">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <a href={d.fileUrl} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 text-sm font-medium text-purple-700 hover:underline">
                        <FileText size={14} className="shrink-0" /> <span className="truncate">{d.fileName}</span>
                      </a>
                      {d.notes && <p className="text-xs text-gray-500 mt-1">{d.notes}</p>}
                      <p className="text-[11px] text-gray-400 mt-1">
                        {d.uploadedAt ? new Date(d.uploadedAt).toLocaleDateString() : ''}{d.uploadedByName ? ` · ${d.uploadedByName}` : ''}
                      </p>
                    </div>
                    <button
                      onClick={() => handleDelete(d.id)}
                      disabled={deletingId === d.id}
                      className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg shrink-0 disabled:opacity-50"
                      title="Delete"
                    >
                      <Trash2 size={15} />
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}

          {error && <p className="text-sm text-red-600">{error}</p>}
        </div>

        <div className="p-4 border-t border-gray-100">
          <button onClick={onClose} className="w-full py-2.5 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-lg transition-colors">Done</button>
        </div>
      </div>
    </div>
  );
}

function TeacherLoginModal({ teacher, onClose }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [showTempPw, setShowTempPw] = useState(false);
  const [tempPw, setTempPw] = useState('');
  const [provisioned, setProvisioned] = useState(!!teacher.authUid);

  const handleCreateLogin = async () => {
    setBusy(true); setError(null); setSuccess(null);
    try {
      const fns = getFunctions();
      const provision = httpsCallable(fns, 'provisionTeacherAccount');
      const res = await provision({ teacherId: teacher.id });
      const email = res?.data?.email || teacher.email;
      setProvisioned(true);
      try {
        await sendPasswordResetEmail(auth, email);
        setSuccess(`Login created. A setup email was sent to ${email}.`);
      } catch (mailErr) {
        setSuccess(`Login created for ${email}, but the setup email failed to send (${mailErr.message}). Use "Send Password Reset Email" to retry.`);
      }
    } catch (err) {
      setError(err.message || 'Failed to create login.');
    } finally {
      setBusy(false);
    }
  };

  const handleSendReset = async () => {
    setBusy(true); setError(null); setSuccess(null);
    try {
      await sendPasswordResetEmail(auth, teacher.email);
      setSuccess(`Password reset email sent to ${teacher.email}.`);
    } catch (err) {
      setError(err.message || 'Failed to send reset email.');
    } finally {
      setBusy(false);
    }
  };

  const handleSetTempPw = async () => {
    if (tempPw.length < 8) { setError('Password must be at least 8 characters.'); return; }
    setBusy(true); setError(null); setSuccess(null);
    try {
      const fns = getFunctions();
      const setPw = httpsCallable(fns, 'setTeacherTempPassword');
      await setPw({ teacherId: teacher.id, newPassword: tempPw });
      setSuccess('Temporary password set. Share it securely and ask the teacher to change it on first login.');
      setTempPw(''); setShowTempPw(false);
    } catch (err) {
      setError(err.message || 'Failed to set password.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6" onClick={e => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-4">
          <div>
            <h3 className="text-lg font-bold text-gray-800">Teacher Login</h3>
            <p className="text-sm text-gray-500">{teacher.name} · {teacher.email || 'no email'}</p>
          </div>
          <button onClick={onClose} className="p-1 text-gray-400 hover:text-gray-600"><X size={18} /></button>
        </div>

        {!provisioned ? (
          <div className="space-y-4">
            <div className="flex items-center gap-2 text-sm text-gray-600 bg-gray-50 rounded-lg p-3">
              <AlertTriangle size={16} className="text-amber-500" />
              No login account yet.
            </div>
            <button
              onClick={handleCreateLogin}
              disabled={busy || !teacher.email}
              className="w-full py-2.5 bg-purple-600 text-white font-medium rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {busy ? 'Working…' : 'Create Login Account'}
            </button>
            {!teacher.email && (
              <p className="text-xs text-red-500">Add an email to this teacher first (edit their profile).</p>
            )}
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center gap-2 text-sm text-green-700 bg-green-50 rounded-lg p-3">
              <CheckCircle size={16} />
              Login active.
            </div>
            <button
              onClick={handleSendReset}
              disabled={busy}
              className="w-full py-2.5 bg-purple-600 text-white font-medium rounded-lg hover:bg-purple-700 disabled:opacity-50"
            >
              {busy ? 'Working…' : 'Send Password Reset Email'}
            </button>
            {!showTempPw ? (
              <button
                onClick={() => { setShowTempPw(true); setError(null); setSuccess(null); }}
                className="w-full py-2 text-gray-600 text-sm border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Set temporary password instead
              </button>
            ) : (
              <div className="space-y-2 border border-gray-200 rounded-lg p-3">
                <label className="block text-xs font-medium text-gray-600">Temporary password (min 8 chars)</label>
                <input
                  type="text"
                  value={tempPw}
                  onChange={e => setTempPw(e.target.value)}
                  placeholder="e.g. Olive-Temp-2026"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                />
                <p className="text-xs text-gray-500">You'll share this with the teacher directly. Ask them to change it on first login.</p>
                <div className="flex gap-2">
                  <button
                    onClick={handleSetTempPw}
                    disabled={busy || tempPw.length < 8}
                    className="flex-1 py-2 bg-orange-500 text-white text-sm font-medium rounded-lg hover:bg-orange-600 disabled:opacity-50"
                  >
                    {busy ? 'Setting…' : 'Confirm & Set'}
                  </button>
                  <button
                    onClick={() => { setShowTempPw(false); setTempPw(''); }}
                    className="px-3 py-2 bg-gray-200 text-gray-700 text-sm rounded-lg hover:bg-gray-300"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {error && <p className="text-sm text-red-600 mt-4">{error}</p>}
        {success && <p className="text-sm text-green-600 mt-4">{success}</p>}
      </div>
    </div>
  );
}

// ─── Main Component ────────────────────────────────────────────────────────────

function TeacherManager() {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [teachers, setTeachers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);

  // Filter
  const [showInactive, setShowInactive] = useState(false);

  // Modals
  const [formModal, setFormModal] = useState(null); // null | { mode: 'add' } | { mode: 'edit', teacher }
  const [triageModal, setTriageModal] = useState(null); // null | { teacher, affectedClasses }
  const [loginModal, setLoginModal] = useState(null); // null | { teacher }
  const [docsModal, setDocsModal] = useState(null); // null | { teacher }
  const [statsModal, setStatsModal] = useState(null); // null | { teacher }
  const [saving, setSaving] = useState(false);

  // Expandable rows
  const [expandedId, setExpandedId] = useState(null);

  // Display timezone for the availability list (does not affect the form entry timezone)
  const [displayTimezone, setDisplayTimezone] = useState(
    () => localStorage.getItem('teacherDisplayTimezone') ?? getBrowserTimezone()
  );
  function handleDisplayTimezoneChange(tz) {
    localStorage.setItem('teacherDisplayTimezone', tz);
    setDisplayTimezone(tz);
  }

  // ── Data loading ────────────────────────────────────────────────────────────

  const fetchTeachers = async () => {
    try {
      setLoading(true);
      setError(null);
      const snap = await getDocs(
        query(collection(db, 'teachers'), orderBy('name'))
      );
      setTeachers(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (err) {
      console.error('Error fetching teachers:', err);
      setError('Failed to load teachers.');
    } finally {
      setLoading(false);
    }
  };

  // Load classes for a specific teacher (used during deactivation triage)
  const fetchClassesForTeacher = async (teacherId) => {
    const snap = await getDocs(
      query(
        collection(db, 'classes'),
        where('teacherId', '==', teacherId),
        where('status', '==', 'active')
      )
    );
    return snap.docs.map(d => ({ id: d.id, ...d.data() }));
  };

  useEffect(() => {
    fetchTeachers();
  }, []);

  // ── Flash message helper ────────────────────────────────────────────────────

  function showSuccess(msg) {
    setSuccessMessage(msg);
    setTimeout(() => setSuccessMessage(null), 4000);
  }

  // ── Save teacher (add or edit) ──────────────────────────────────────────────
  // availability[] arrives already converted to UTC from TeacherFormModal.handleSubmit.

  const handleSaveTeacher = async (form) => {
    setSaving(true);
    try {
      const isEditing = !!form.id;
      const payload = {
        name: form.name.trim(),
        email: form.email?.trim() ?? '',
        phone: form.phone?.trim() ?? '',
        timezone: form.timezone ?? '',
        country: form.country ?? '',
        paymentMethod: form.paymentMethod ?? '',
        paymentDetail: form.paymentDetail ?? '',
        dialect: form.dialect,
        color: form.color,
        availability: form.availability ?? [],
        active: form.active ?? true,
        // All availability times are stored in UTC. Conversion from the admin's
        // entry timezone is applied in TeacherFormModal before onSave is called.
        availabilityTimezone: 'UTC',
      };

      if (isEditing) {
        await updateDoc(doc(db, 'teachers', form.id), payload);
        showSuccess(`${form.name} updated successfully.`);
      } else {
        payload.createdAt = new Date().toISOString();
        await addDoc(collection(db, 'teachers'), payload);
        showSuccess(`${form.name} added successfully.`);
      }

      setFormModal(null);
      await fetchTeachers();
    } catch (err) {
      console.error('Save teacher error:', err);
      setError('Failed to save teacher. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  // ── Deactivate flow ─────────────────────────────────────────────────────────

  const handleDeactivateClick = async (teacher) => {
    // Check for active classes first
    const affectedClasses = await fetchClassesForTeacher(teacher.id);

    if (affectedClasses.length === 0) {
      // No classes — deactivate immediately with confirmation
      if (!window.confirm(`Mark ${teacher.name} as inactive? They have no active classes.`)) return;
      try {
        await updateDoc(doc(db, 'teachers', teacher.id), { active: false });
        showSuccess(`${teacher.name} marked inactive.`);
        await fetchTeachers();
      } catch (err) {
        console.error('Deactivate error:', err);
        setError('Failed to deactivate teacher.');
      }
      return;
    }

    // Has active classes — open triage
    setTriageModal({ teacher, affectedClasses });
  };

  // ── Reactivate ──────────────────────────────────────────────────────────────

  const handleReactivate = async (teacher) => {
    if (!window.confirm(`Reactivate ${teacher.name}?`)) return;
    try {
      await updateDoc(doc(db, 'teachers', teacher.id), { active: true });
      showSuccess(`${teacher.name} reactivated.`);
      await fetchTeachers();
    } catch (err) {
      console.error('Reactivate error:', err);
      setError('Failed to reactivate teacher.');
    }
  };

  // ── Delete teacher ──────────────────────────────────────────────────────────

  const handleDeleteTeacher = async (teacher) => {
    if (!window.confirm(
      `Permanently delete ${teacher.name}? This cannot be undone. Make sure they have no active classes first.`
    )) return;
    try {
      await deleteDoc(doc(db, 'teachers', teacher.id));
      showSuccess(`${teacher.name} deleted.`);
      await fetchTeachers();
    } catch (err) {
      console.error('Delete teacher error:', err);
      setError('Failed to delete teacher.');
    }
  };

  // ── Triage resolve ──────────────────────────────────────────────────────────

  const handleTriageResolve = async (resolvedMap) => {
    const { teacher } = triageModal;

    // Apply each resolution
    const updates = Object.entries(resolvedMap).map(async ([classId, resolution]) => {
      if (resolution.action === 'reassign') {
        const newTeacher = triageModal.affectedClasses
          .find(c => c.id === classId) ?? {};
        const replacementTeacher = teachers.find(t => t.id === resolution.replacementTeacherId);
        await updateDoc(doc(db, 'classes', classId), {
          teacherId:   resolution.replacementTeacherId,
          teacherName: replacementTeacher?.name ?? '',
        });
      } else if (resolution.action === 'suspend') {
        await updateDoc(doc(db, 'classes', classId), { status: 'suspended' });
      }
    });

    await Promise.all(updates);

    // Mark teacher inactive
    await updateDoc(doc(db, 'teachers', teacher.id), { active: false });
    showSuccess(`${teacher.name} marked inactive. Classes resolved.`);
    setTriageModal(null);
    await fetchTeachers();
  };

  // ── Derived lists ───────────────────────────────────────────────────────────

  const activeTeachers    = teachers.filter(t => t.active);
  const displayedTeachers = showInactive ? teachers : activeTeachers;

  // ── Render ──────────────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading teachers...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-5xl mx-auto p-6 space-y-5">

        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/admin')}
              className="p-2 hover:bg-gray-200 rounded-lg transition-colors"
            >
              <ArrowLeft size={20} />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-gray-800">Teachers</h1>
              <p className="text-sm text-gray-500">
                {activeTeachers.length} active · {teachers.length - activeTeachers.length} inactive
              </p>
            </div>
          </div>
          <select
            value={displayTimezone}
            onChange={e => handleDisplayTimezoneChange(e.target.value)}
            className="border border-gray-200 rounded-lg px-3 py-2 text-sm text-gray-700 bg-white focus:outline-none focus:ring-2 focus:ring-purple-500"
            title="Display timezone for availability times"
          >
            {COMMON_TIMEZONES.map(tz => (
              <option key={tz.value} value={tz.value}>{tz.label}</option>
            ))}
          </select>
          <button
            onClick={() => setFormModal({ mode: 'add' })}
            className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white text-sm font-medium rounded-xl hover:bg-purple-700 transition-colors"
          >
            <Plus size={16} />
            Add Teacher
          </button>
        </div>

        {/* Flash messages */}
        {successMessage && (
          <div className="flex items-center gap-2 px-4 py-3 bg-green-50 border border-green-200 rounded-xl text-sm text-green-700">
            <CheckCircle size={16} />
            {successMessage}
          </div>
        )}
        {error && (
          <div className="flex items-center gap-2 px-4 py-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700">
            <AlertTriangle size={16} />
            {error}
            <button onClick={() => setError(null)} className="ml-auto"><X size={14} /></button>
          </div>
        )}

        {/* Filter toggle */}
        <div className="flex items-center gap-3">
          <button
            onClick={() => setShowInactive(v => !v)}
            className={`text-sm px-3 py-1.5 rounded-lg border transition-colors ${
              showInactive
                ? 'bg-gray-200 border-gray-300 text-gray-700'
                : 'bg-white border-gray-200 text-gray-500 hover:bg-gray-50'
            }`}
          >
            {showInactive ? 'Hide inactive' : 'Show inactive'}
          </button>
        </div>

        {/* Teacher table */}
        {displayedTeachers.length === 0 ? (
          <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
            <Users size={40} className="text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500 font-medium">No teachers yet</p>
            <p className="text-sm text-gray-400 mt-1">Add your first teacher to get started.</p>
          </div>
        ) : (
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200 bg-gray-50">
                  <th className="text-left px-4 py-3 font-semibold text-gray-600">Teacher</th>
                  <th className="text-left px-4 py-3 font-semibold text-gray-600">Dialect</th>
                  <th className="text-left px-4 py-3 font-semibold text-gray-600">Availability</th>
                  <th className="text-left px-4 py-3 font-semibold text-gray-600">Status</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {displayedTeachers.map(teacher => (
                  <React.Fragment key={teacher.id}>
                    <tr
                      className="hover:bg-gray-50 transition-colors cursor-pointer"
                      onClick={() => setExpandedId(expandedId === teacher.id ? null : teacher.id)}
                    >
                      {/* Name + color dot */}
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <span
                            className="w-3 h-3 rounded-full flex-shrink-0"
                            style={{ backgroundColor: teacher.color ?? '#7C3AED' }}
                          />
                          <span className="font-medium text-gray-800">{teacher.name}</span>
                          <AccountBadge teacher={teacher} />
                        </div>
                      </td>

                      {/* Dialect */}
                      <td className="px-4 py-3 text-gray-600">{dialectLabel(teacher.dialect)}</td>

                      {/* Availability summary */}
                      <td className="px-4 py-3 text-gray-500">
                        {teacher.availability?.length > 0
                          ? `${teacher.availability.length} slot${teacher.availability.length !== 1 ? 's' : ''}`
                          : <span className="italic text-gray-400">None set</span>
                        }
                      </td>

                      {/* Status */}
                      <td className="px-4 py-3">
                        <StatusBadge active={teacher.active} />
                      </td>

                      {/* Row actions */}
                      <td className="px-4 py-3">
                        <div
                          className="flex items-center justify-end gap-1"
                          onClick={e => e.stopPropagation()}
                        >
                          <button
                            onClick={() => setFormModal({ mode: 'edit', teacher })}
                            className="p-1.5 text-gray-400 hover:text-purple-600 hover:bg-purple-50 rounded-lg transition-colors"
                            title="Edit"
                          >
                            <Edit2 size={15} />
                          </button>
                          {isSuperAdmin(user?.uid) && (
                            <button
                              onClick={() => setLoginModal({ teacher })}
                              className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                              title={teacher.authUid ? 'Manage login' : 'Create login'}
                            >
                              <KeyRound size={15} />
                            </button>
                          )}
                          <button
                            onClick={() => setDocsModal({ teacher })}
                            className="p-1.5 text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
                            title="Documents"
                          >
                            <FileText size={15} />
                          </button>
                          <button
                            onClick={() => setStatsModal({ teacher })}
                            className="p-1.5 text-gray-400 hover:text-emerald-600 hover:bg-emerald-50 rounded-lg transition-colors"
                            title="Stats"
                          >
                            <BarChart3 size={15} />
                          </button>
                          {teacher.active ? (
                            <button
                              onClick={() => handleDeactivateClick(teacher)}
                              className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                              title="Deactivate"
                            >
                              <UserX size={15} />
                            </button>
                          ) : (
                            <>
                              <button
                                onClick={() => handleReactivate(teacher)}
                                className="p-1.5 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                                title="Reactivate"
                              >
                                <UserCheck size={15} />
                              </button>
                              <button
                                onClick={() => handleDeleteTeacher(teacher)}
                                className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                title="Delete permanently"
                              >
                                <Trash2 size={15} />
                              </button>
                            </>
                          )}
                          <span className="text-gray-300 ml-1">
                            {expandedId === teacher.id
                              ? <ChevronUp size={15} />
                              : <ChevronDown size={15} />
                            }
                          </span>
                        </div>
                      </td>
                    </tr>

                    {/* Expanded availability detail */}
                    {expandedId === teacher.id && (
                      <tr className="bg-purple-50">
                        <td colSpan={5} className="px-6 py-4">
                          <p className="text-xs font-semibold text-purple-700 uppercase tracking-wide mb-2">
                            Availability Schedule
                            <span className="ml-2 text-purple-400 font-normal normal-case">
                              (hover for timezone conversions · stored in UTC)
                            </span>
                          </p>
                          {teacher.availability?.length > 0 ? (
                            <div className="flex flex-wrap gap-2">
                              {teacher.availability.map((slot, i) => (
                                <TimezoneTooltip key={i} timeStr={slot.startTime} dayOfWeek={slot.dayOfWeek}>
                                  <span
                                    className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white border border-purple-200 text-purple-800 text-xs rounded-lg cursor-help"
                                    title="Hover to see other timezones"
                                  >
                                    <Clock size={11} />
                                    {(() => {
                                      const { localDay } = timeFromUTC(slot.startTime, slot.dayOfWeek, displayTimezone);
                                      return DAYS_OF_WEEK.find(d => d.value === localDay)?.label ?? localDay;
                                    })()}{' '}
                                    {formatTimeDisplay(slot.startTime, displayTimezone)} – {formatTimeDisplay(slot.endTime, displayTimezone)}{' '}
                                    <span className="text-purple-400">{COMMON_TIMEZONES.find(tz => tz.value === displayTimezone)?.label ?? displayTimezone}</span>
                                  </span>
                                </TimezoneTooltip>
                              ))}
                            </div>
                          ) : (
                            <p className="text-xs text-gray-400 italic">No availability slots recorded.</p>
                          )}
                          {teacher.notes && (
                            <p className="text-xs text-gray-500 mt-2 italic">"{teacher.notes}"</p>
                          )}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Add / Edit modal */}
      {formModal && (
        <TeacherFormModal
          teacher={formModal.mode === 'edit' ? formModal.teacher : null}
          onSave={handleSaveTeacher}
          onClose={() => setFormModal(null)}
          saving={saving}
        />
      )}

      {loginModal && (
        <TeacherLoginModal
          teacher={loginModal.teacher}
          onClose={() => { setLoginModal(null); fetchTeachers(); }}
        />
      )}

      {docsModal && (
        <TeacherDocumentsModal
          teacher={docsModal.teacher}
          onClose={() => setDocsModal(null)}
        />
      )}

      {statsModal && (
        <TeacherStatsModal
          teacher={statsModal.teacher}
          onClose={() => setStatsModal(null)}
        />
      )}

      {/* Reassign triage modal */}
      {triageModal && (
        <ReassignTriageModal
          teacher={triageModal.teacher}
          affectedClasses={triageModal.affectedClasses}
          activeTeachers={activeTeachers}
          onResolve={handleTriageResolve}
          onClose={() => setTriageModal(null)}
        />
      )}
    </div>
  );
}

export default TeacherManager;